import { Fragment, useId } from "react";
import { X } from "lucide-react";

import { Dialog } from "~/components/Dialog";
import {
  SHORTCUT_SECTIONS,
  SHORTCUTS,
  formatShortcutKeyParts,
  isMacPlatform,
} from "~/lib/shortcuts";
import { ICON_STROKE } from "~/lib/icon";

export interface ShortcutDialogProps {
  onClose: () => void;
  platformLabel?: string;
}

export function ShortcutDialog({ onClose, platformLabel }: ShortcutDialogProps) {
  const titleId = useId();
  const isMac = isMacPlatform(platformLabel);
  const splitIndex = Math.ceil(SHORTCUT_SECTIONS.length / 2);
  const leftSections = SHORTCUT_SECTIONS.slice(0, splitIndex);
  const rightSections = SHORTCUT_SECTIONS.slice(splitIndex);

  return (
    <Dialog
      onClose={onClose}
      labelledBy={titleId}
      width="640px"
      panelStyle={{ height: 560, maxWidth: "calc(100% - var(--chat-space-8))" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-3)",
          height: 56,
          padding: "0 var(--chat-space-4) 0 var(--chat-space-6)",
          flexShrink: 0,
        }}
      >
        <h2
          id={titleId}
          style={{
            flex: 1,
            margin: 0,
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-display)",
            fontSize: "var(--chat-type-display-sm-size)",
            lineHeight: "var(--chat-type-display-sm-line)",
            fontWeight: 600,
          }}
        >
          Keyboard shortcuts
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close keyboard shortcuts"
          className="chat-button"
          style={{
            width: "var(--chat-dialog-close-size)",
            height: "var(--chat-dialog-close-size)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            background: "transparent",
            color: "var(--chat-fg-secondary)",
            borderRadius: "var(--chat-radius-sm)",
            cursor: "pointer",
          }}
        >
          <X size={14} strokeWidth={ICON_STROKE} aria-hidden />
        </button>
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "var(--chat-space-4) var(--chat-space-6)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))",
            gap: "var(--chat-space-6)",
          }}
        >
          <SectionColumn sections={leftSections} isMac={isMac} />
          <SectionColumn sections={rightSections} isMac={isMac} />
        </div>
      </div>
      <div
        style={{
          height: 40,
          display: "flex",
          alignItems: "center",
          padding: "0 var(--chat-space-6)",
          color: "var(--chat-fg-muted)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-xs-size)",
          lineHeight: "var(--chat-type-body-xs-line)",
          flexShrink: 0,
        }}
      >
        Press Esc to close
      </div>
    </Dialog>
  );
}

function SectionColumn({
  sections,
  isMac,
}: {
  sections: typeof SHORTCUT_SECTIONS;
  isMac: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--chat-space-6)" }}>
      {sections.map((section) => (
        <ShortcutSection key={section.id} section={section} isMac={isMac} />
      ))}
    </div>
  );
}

function ShortcutSection({
  section,
  isMac,
}: {
  section: (typeof SHORTCUT_SECTIONS)[number];
  isMac: boolean;
}) {
  const shortcuts = SHORTCUTS.filter((shortcut) => shortcut.section === section.id);
  if (shortcuts.length === 0) return null;

  return (
    <section>
      <h3
        style={{
          margin: "0 0 var(--chat-space-2)",
          color: "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-xs-size)",
          lineHeight: "var(--chat-type-body-xs-line)",
          fontWeight: 400,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {section.label}
      </h3>
      <div>
        {shortcuts.map((shortcut) => (
          <div
            key={shortcut.id}
            style={{
              minHeight: 32,
              display: "flex",
              alignItems: "center",
              gap: "var(--chat-space-3)",
            }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                color: "var(--chat-fg-primary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-md-size)",
                lineHeight: "var(--chat-type-body-md-line)",
              }}
            >
              {shortcut.description}
            </span>
            <span
              style={{
                flexShrink: 0,
                color: "var(--chat-fg-secondary)",
                fontFamily: "var(--chat-font-mono)",
                fontSize: "var(--chat-type-body-xs-size)",
                lineHeight: "var(--chat-type-body-xs-line)",
                display: "inline-flex",
                alignItems: "center",
                gap: "var(--chat-space-1)",
              }}
            >
              {formatShortcutKeyParts(shortcut, isMac).map((combo, comboIndex) => (
                <Fragment key={`${shortcut.id}-${comboIndex}`}>
                  {comboIndex > 0 && <span aria-hidden="true">/</span>}
                  {combo.map((key, keyIndex) => (
                    <Fragment key={`${shortcut.id}-${comboIndex}-${keyIndex}`}>
                      {keyIndex > 0 && <span aria-hidden="true">+</span>}
                      <kbd
                        style={{
                          padding: "calc(var(--chat-space-1) / 2) calc(var(--chat-space-5) / 2)",
                          borderRadius: "var(--chat-radius-sm)",
                          background: "var(--chat-surface-raised)",
                          font: "inherit",
                        }}
                      >
                        {key}
                      </kbd>
                    </Fragment>
                  ))}
                </Fragment>
              ))}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
